// src/pages/finance-portal/reports/Forecasting.js

import React, { useState } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Alert, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    FormControl, InputLabel, Select, MenuItem, CircularProgress, Stack, Chip,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatCurrency, todayIso,
} from './reportShared';

export default function Forecasting() {
    const [metric, setMetric] = useState('revenue');
    const [method, setMethod] = useState('LINEAR_REGRESSION');
    const [horizon, setHorizon] = useState(12);
    const [historical, setHistorical] = useState(24);
    const [confidence, setConfidence] = useState(95);
    const [end, setEnd] = useState(todayIso());
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const run = async () => {
        setLoading(true); setError(null);
        try {
            const res = await axiosInstance.post('/api/finance/analytics/forecast', {
                metric, method,
                horizon_months: horizon,
                historical_months: historical,
                confidence_level: confidence,
                end_date: end,
            });
            if (res.data.success) setResult(res.data);
            else setError(res.data.message || 'Failed');
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to run forecast');
        } finally { setLoading(false); }
    };

    const save = async () => {
        if (!result) return;
        const name = prompt('Forecast name?');
        if (!name) return;
        try {
            await axiosInstance.post('/api/finance/forecasts', {
                name,
                metric_key: metric,
                method,
                horizon_months: horizon,
                historical_months: historical,
                confidence_level: confidence,
                historical_data: result.historical,
                projection: result.projection,
            });
            alert('Forecast saved');
        } catch (e) {
            alert(e.response?.data?.message || 'Failed to save');
        }
    };

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <TimelineIcon sx={{ color: ACCENT }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>Forecasting</Typography>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Metric</InputLabel>
                            <Select label="Metric" value={metric}
                                onChange={(e) => setMetric(e.target.value)}>
                                <MenuItem value="revenue">Revenue</MenuItem>
                                <MenuItem value="expense">Expense</MenuItem>
                                <MenuItem value="net_income">Net Income</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Method</InputLabel>
                            <Select label="Method" value={method}
                                onChange={(e) => setMethod(e.target.value)}>
                                <MenuItem value="LINEAR_REGRESSION">Linear Regression</MenuItem>
                                <MenuItem value="EXPONENTIAL_SMOOTHING">Exponential Smoothing</MenuItem>
                                <MenuItem value="SEASONAL_DECOMPOSITION">Seasonal Decomposition</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={4} md={1}>
                        <TextField label="Horizon" type="number" fullWidth size="small"
                            value={horizon} onChange={(e) => setHorizon(parseInt(e.target.value, 10))} />
                    </Grid>
                    <Grid item xs={4} md={2}>
                        <TextField label="Historical (mo)" type="number" fullWidth size="small"
                            value={historical} onChange={(e) => setHistorical(parseInt(e.target.value, 10))} />
                    </Grid>
                    <Grid item xs={4} md={1}>
                        <TextField label="Confidence" type="number" fullWidth size="small"
                            value={confidence} onChange={(e) => setConfidence(parseInt(e.target.value, 10))} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField label="End" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={end} onChange={(e) => setEnd(e.target.value)} />
                    </Grid>
                    <Grid item xs={6} md={1} sx={{ textAlign: 'right' }}>
                        <Button onClick={run} variant="contained"
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}>Run</Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {result && !loading && (
                <Paper sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={1}>
                            <Chip label={`${result.method}`} />
                            <Chip label={`${result.horizon_months} month horizon`} variant="outlined" />
                            <Chip label={`${result.confidence_level}% CI`} variant="outlined" />
                        </Stack>
                        <Button onClick={save} variant="outlined">Save Forecast</Button>
                    </Stack>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>Historical</Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Period</TableCell>
                                    <TableCell align="right">Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {result.historical.map((h) => (
                                    <TableRow key={h.period}>
                                        <TableCell>{h.period}</TableCell>
                                        <TableCell align="right">{formatCurrency(h.value)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>Projection</Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Period</TableCell>
                                    <TableCell align="right">Forecast</TableCell>
                                    <TableCell align="right">Lower Bound</TableCell>
                                    <TableCell align="right">Upper Bound</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {result.projection.map((p) => (
                                    <TableRow key={p.period}>
                                        <TableCell>{p.period}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, color: ACCENT }}>
                                            {formatCurrency(p.value)}
                                        </TableCell>
                                        <TableCell align="right">{formatCurrency(p.lower)}</TableCell>
                                        <TableCell align="right">{formatCurrency(p.upper)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
}
